import clsx from "clsx";
import React from "react";
import Skeleton from "react-loading-skeleton";
import { Link, type LinkProps } from "react-router-dom";

import { MeetingState, type TMeeting } from "~/api";
import { buildLocale } from "~/common/locale";
import { formatPersonName, PersonName } from "~/components/common/PersonName";
import { Button } from "~/components/ui/button";
import { Img } from "~/components/ui/image";
import { API_MEETINGS_IMAGE } from "~/config";

import { ZoomJoinButton } from "../events/event_page";
import expertNoAvatarImg from "./expert-no-avatar.png";

export function ExpertImage({ expert }: { expert: TMeeting["expert"] | undefined }) {
  const imageSkeleton = <Skeleton width={75} height={75} className="tw-rounded-full" />;

  return expert ? (
    <Img
      width={75}
      height={75}
      errorFallback={
        <Img
          width={75}
          height={75}
          errorFallback={null}
          loadingFallback={imageSkeleton}
          src={expertNoAvatarImg}
        />
      }
      loadingFallback={imageSkeleton}
      src={expert.avatar ? API_MEETINGS_IMAGE(expert.account_id, expert.avatar) : expertNoAvatarImg}
    />
  ) : (
    imageSkeleton
  );
}

export function ExpertName({
  meeting,
  className,
}: {
  meeting: TMeeting | undefined;
  className?: string;
}) {
  return (
    <div className={clsx("tw-flex tw-flex-col tw-leading-9", className)}>
      {meeting ? (
        <>
          <span className="tw-text-gray-400">Эксперт</span>
          <PersonName person={meeting.expert} useMiddleName isDoctor />
        </>
      ) : (
        <>
          <Skeleton className="tw-rounded" width={175} />
          <Skeleton className="tw-rounded" width={175} />
        </>
      )}
    </div>
  );
}

export function PatientName({
  patient,
  className,
}: {
  patient: TMeeting["patients"][0] | undefined;
  className?: string;
}) {
  return (
    <div className={clsx("tw-flex tw-flex-col tw-items-start tw-gap-1", className)}>
      {patient ? (
        <>
          <span className="tw-text-gray-400">Пациент</span>
          <Link to={`/pages/patient/${patient.patient_id}`}>
            <PersonName person={patient} useMiddleName isDoctor /> {patient.patient_id}
          </Link>
        </>
      ) : (
        <>
          <Skeleton className="tw-rounded" width={200} />
          <Skeleton className="tw-rounded" width={200} />
        </>
      )}
    </div>
  );
}

export function EmptyDisplay({
  title,
  description,
  link,
  isError = false,
}: {
  title: React.ReactNode;
  description: React.ReactNode;
  link?: LinkProps;
  isError?: boolean;
}) {
  return (
    <div className="tw-mb-10 tw-flex tw-grow tw-flex-col tw-items-center tw-justify-center tw-space-y-4 tw-p-3.5 tw-text-center">
      <strong className={clsx("tw-text-base", { "tw-text-red-500": isError })}>{title}</strong>
      <p className="tw-my-0 tw-text-gray-400">{description}</p>
      {link ? (
        <Button rounded className="tw-uppercase" asChild>
          <Link {...link}>{link.children}</Link>
        </Button>
      ) : null}
    </div>
  );
}

export function ZoomButton({ meeting }: { meeting: TMeeting | undefined }) {
  const className = "tw-rounded-md max-sm:tw-uppercase";

  if (!meeting) {
    return <Skeleton className={className} width={150} height={36} />;
  }

  if (meeting.zoom_join_url) {
    return (
      <ZoomJoinButton
        title={`Встреча с клиническим экспертом: ${formatPersonName({
          person: meeting.expert,
          useMiddleName: true,
          isDoctor: true,
        })}`}
        zoom_join_url={meeting.zoom_join_url}
        className={className}
      >
        Перейти в Zoom
      </ZoomJoinButton>
    );
  }

  return null;
}

export function StateBadge({
  meeting,
  className: classNameProp,
  short = false,
}: {
  meeting: TMeeting | undefined;
  className?: string;
  short?: boolean;
}) {
  let className = "tw-inline-block tw-max-w-fit tw-rounded-full tw-px-3 tw-text-xs tw-text-black";
  let text = "";

  if (!meeting) {
    return (
      <Skeleton width={short ? 100 : 200} className={clsx("tw-rounded-full", classNameProp)} />
    );
  }

  if (meeting.state == MeetingState.NEW && meeting.is_finished) {
    return null;
  }

  switch (meeting.state) {
    case MeetingState.NEW:
      className = clsx(className, "tw-bg-green-200");
      text = short ? "Вы записаны" : "Вы записаны на консультацию";
      break;
    case MeetingState.CANCELLED:
      if (meeting.rescheduled_meeting_id) {
        className = clsx(className, "tw-bg-blue-200");
        text = short ? "Перенос" : "Консультация перенесена";
      } else {
        className = clsx(className, "tw-bg-red-300");
        text = short ? "Отмена" : "Консультация отменена";
      }
      break;
  }

  return <span className={clsx(className, classNameProp)}>{text}</span>;
}

export function formatStartDate(startTime: string) {
  const startDate = new Date(startTime);
  const dateStr = startDate.toLocaleDateString(buildLocale, { month: "long", day: "numeric" });
  const weekdayStr = startDate.toLocaleDateString(buildLocale, { weekday: "long" });
  const timeStr = startDate.toLocaleTimeString(buildLocale, { hour: "2-digit", minute: "2-digit" });
  return `${dateStr}, ${weekdayStr}, в ${timeStr} (Мск)`;
}
